// xAI / Grok Service Health Dashboard - production page
// Data: live from status.x.ai RSS feed
import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Flashbar from '@cloudscape-design/components/flashbar'
import Header from '@cloudscape-design/components/header'
import Spinner from '@cloudscape-design/components/spinner'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import TextFilter from '@cloudscape-design/components/text-filter'
import Select from '@cloudscape-design/components/select'
import {
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorBackgroundContainerContent,
  colorBorderDividerDefault,
  colorTextStatusError,
  colorTextStatusSuccess,
  colorTextAccent,
} from '@cloudscape-design/design-tokens'

// ── Constants ─────────────────────────────────────────────────────────────────
const RSS_URL = 'https://status.x.ai/feed.xml'
const CORS_PROXY = (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`

const SERVICE_MAP: Record<string, string> = {
  'API (us-east-1.api.x.ai)': 'API us-east-1',
  'API (eu-west-1.api.x.ai)': 'API eu-west-1',
  'Grok (Web)': 'Grok Web',
  'Grok (iOS)': 'Grok iOS',
  'Grok (Android)': 'Grok Android',
  'Grok in X': 'Grok in X',
  'API Console': 'API Console',
  'Single Sign-On': 'SSO',
  'Docs': 'Docs',
  'xAI Website': 'xAI Website',
}

// xAI branding: blue/cyan tones
const SERVICE_COLORS: Record<string, string> = {
  'API us-east-1': '#38bdf8',
  'API eu-west-1': '#22d3ee',
  'Grok Web': '#818cf8',
  'Grok iOS': '#a78bfa',
  'Grok Android': '#34d399',
  'Grok in X': '#60a5fa',
  'API Console': '#f472b6',
  'SSO': '#fbbf24',
  'Docs': '#94a3b8',
  'xAI Website': '#c084fc',
}
const FALLBACK_PALETTE = ['#38bdf8','#22d3ee','#818cf8','#a78bfa','#34d399','#60a5fa','#f472b6','#fbbf24','#94a3b8','#c084fc']
let paletteIdx = 0
function getColor(service: string): string {
  if (SERVICE_COLORS[service]) return SERVICE_COLORS[service]
  const c = FALLBACK_PALETTE[paletteIdx++ % FALLBACK_PALETTE.length]
  SERVICE_COLORS[service] = c
  return c
}

// ── Types ────────────────────────────────────────────────────────────────────
interface GrokIncident {
  id: string
  title: string
  service: string
  startDate: Date
  resolvedDate: Date | null
  endCapped: Date
  durationMs: number
  durationMin: number
  status: string
  severity: 'degraded' | 'down'
  ongoing: boolean
  link: string
  updates: string[]
}

interface ServiceMetric {
  entity: string
  count: number
  availability: number
  mttr: number
  totalDowntimeMs: number
  longest: GrokIncident | null
}

// ── RSS parsing helpers ──────────────────────────────────────────────────────
function extractService(title: string): string {
  // Title format: "[Service Name] Incident Title"
  const match = title.match(/^\[([^\]]+)\]/)
  if (match) {
    const raw = match[1].trim()
    return SERVICE_MAP[raw] || raw
  }
  // Fallback: check if title starts with a known service name
  for (const [key, short] of Object.entries(SERVICE_MAP)) {
    if (title.startsWith(key)) return short
  }
  return 'Unknown'
}

function stripIncidentPrefix(title: string): string {
  return title.replace(/^\[[^\]]+\]\s*/, '')
}

function parseDescription(html: string): {
  status: string
  severity: 'degraded' | 'down'
  resolvedDate: Date | null
  updates: string[]
} {
  const div = document.createElement('div')
  div.innerHTML = html

  let status = 'unknown'
  let severity: 'degraded' | 'down' = 'degraded'
  let resolvedDate: Date | null = null
  const updates: string[] = []

  const text = div.textContent || ''

  // Extract status
  const statusMatch = text.match(/Status:\s*(\w+)/i)
  if (statusMatch) status = statusMatch[1].toLowerCase()

  // Extract severity from explicit field AND from incident content
  const sevMatch = text.match(/Severity:\s*(\w+)/i)
  if (sevMatch) {
    const sev = sevMatch[1].toLowerCase()
    severity = (sev === 'major' || sev === 'critical' || sev === 'outage') ? 'down' : 'degraded'
  }

  // xAI doesn't use standard severity labels - detect from content keywords
  const lowerText = text.toLowerCase()
  const downKeywords = ['unavailable', 'outage', 'all models are currently unavailable', 'models unavailable', 'experiencing an outage']
  const degradedKeywords = ['slow', 'latency', 'increased error', 'degraded', 'delayed', 'disrupted', 'partially']
  if (downKeywords.some(k => lowerText.includes(k))) {
    severity = 'down'
  } else if (degradedKeywords.some(k => lowerText.includes(k))) {
    severity = 'degraded'
  }

  // If status indicates resolved/available, try to find resolved date
  if (status === 'resolved' || status === 'available') {
    const resolvedMatch = text.match(/Resolved:\s*(.+?)(?:\n|$)/i)
      || text.match(/Resolved at:\s*(.+?)(?:\n|$)/i)
    if (resolvedMatch) {
      const d = new Date(resolvedMatch[1].trim())
      if (!isNaN(d.getTime())) resolvedDate = d
    }
  }

  // Extract update entries (lines that look like timestamps or update blocks)
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  lines.forEach(line => {
    if (line.length > 10 && !line.startsWith('Status:') && !line.startsWith('Severity:') && !line.startsWith('Resolved:')) {
      updates.push(line)
    }
  })

  return { status, severity, resolvedDate, updates }
}

function parseRssFeed(xmlText: string): GrokIncident[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlText, 'text/xml')
  const items = doc.querySelectorAll('item')
  const incidents: GrokIncident[] = []

  items.forEach(item => {
    const title = item.querySelector('title')?.textContent || ''
    const guid = item.querySelector('guid')?.textContent || ''
    const link = item.querySelector('link')?.textContent || ''
    const pubDate = item.querySelector('pubDate')?.textContent || ''
    const description = item.querySelector('description')?.textContent || ''
    const categories = Array.from(item.querySelectorAll('category')).map(c => c.textContent?.toLowerCase() || '')

    const service = extractService(title)
    const startDate = new Date(pubDate)
    if (isNaN(startDate.getTime())) return

    const parsed = parseDescription(description)

    // Use categories to refine status
    const isResolved = categories.includes('resolved') || categories.includes('available') || parsed.status === 'resolved'
    const resolvedDate = isResolved ? parsed.resolvedDate : null
    const endCapped = resolvedDate ?? new Date()
    const durationMs = Math.max(0, endCapped.getTime() - startDate.getTime())

    // Determine severity from categories, parsed description, or title keywords
    let severity = parsed.severity
    if (categories.includes('major-outage') || categories.includes('outage')) severity = 'down'
    // Also check title for outage keywords
    const lowerTitle = title.toLowerCase()
    if (lowerTitle.includes('unavailable') || lowerTitle.includes('outage')) severity = 'down'

    incidents.push({
      id: guid || `inc-${startDate.getTime()}`,
      title: stripIncidentPrefix(title) || title,
      service,
      startDate,
      resolvedDate,
      endCapped,
      durationMs,
      durationMin: durationMs / 60000,
      status: isResolved ? 'resolved' : (parsed.status || 'investigating'),
      severity,
      ongoing: !isResolved,
      link,
      updates: parsed.updates,
    })
  })

  return incidents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
}

// ── Utility functions ────────────────────────────────────────────────────────
function mergeIntervals(intervals: [number, number][]): [number, number][] {
  if (!intervals.length) return []
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    if (sorted[i][0] <= last[1]) last[1] = Math.max(last[1], sorted[i][1])
    else merged.push(sorted[i])
  }
  return merged
}

function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000)
  if (totalMin < 60) return totalMin + 'm'
  const h = Math.floor(totalMin / 60), m = totalMin % 60
  if (h < 24) return h + 'h ' + m + 'm'
  return Math.floor(h / 24) + 'd ' + (h % 24) + 'h'
}

function fmtDateShort(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })
}

// ── Shared chart tooltip style ────────────────────────────────────────────────
const tooltipStyle = {
  backgroundColor: colorBackgroundContainerContent,
  border: `1px solid ${colorBorderDividerDefault}`,
  borderRadius: 6, fontSize: 12, color: colorTextBodyDefault,
}

// ── Availability ring (cyan accent) ───────────────────────────────────────────
function AvailRing({ pct }: { pct: number }) {
  const color = pct >= 99 ? colorTextStatusSuccess : pct >= 95 ? '#f97316' : colorTextStatusError
  const deg = (pct / 100) * 360
  return (
    <div style={{
      width: 84, height: 84, borderRadius: '50%',
      background: `conic-gradient(${color} ${deg}deg, ${colorBorderDividerDefault} ${deg}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: colorBackgroundContainerContent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color,
      }}>
        {pct.toFixed(2)}%
      </div>
    </div>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, detail, onClick }: {
  label: string; value: string | number; detail: string; onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colorBackgroundContainerContent,
        border: `1px solid ${colorBorderDividerDefault}`,
        borderRadius: 8, padding: '14px 16px', textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box variant="small" color="text-body-secondary">{label}</Box>
      <Box variant="h2">{value}</Box>
      <Box variant="small" color="text-body-secondary">{detail}</Box>
    </div>
  )
}

// ── Entity cards ──────────────────────────────────────────────────────────────
function EntityCards({ metrics }: { metrics: ServiceMetric[] }) {
  if (!metrics.length) return null
  return (
    <Container header={<Header variant="h2">xAI Service Availability</Header>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {metrics.map(m => (
          <div key={m.entity} style={{
            border: `1px solid ${colorBorderDividerDefault}`,
            borderRadius: 8, padding: 16, textAlign: 'center',
            background: colorBackgroundContainerContent,
          }}>
            <Box fontWeight="bold" fontSize="body-m">
              <span style={{ color: colorTextBodyDefault }}>{m.entity}</span>
            </Box>
            <div style={{ margin: '12px auto', width: 'fit-content' }}>
              <AvailRing pct={m.availability} />
            </div>
            <Box variant="small" color="text-body-secondary">
              Incidents: <strong style={{ color: colorTextBodyDefault }}>{m.count}</strong><br />
              Downtime: <strong style={{ color: colorTextBodyDefault }}>{formatDuration(m.totalDowntimeMs)}</strong><br />
              MTTR: <strong style={{ color: colorTextBodyDefault }}>{Math.round(m.mttr)}m</strong>
            </Box>
          </div>
        ))}
      </div>
    </Container>
  )
}

// ── Availability chart ────────────────────────────────────────────────────────
function AvailabilityChart({ metrics }: { metrics: ServiceMetric[] }) {
  const minVal = Math.max(80, Math.floor(Math.min(...metrics.map(m => m.availability))) - 1)
  return (
    <Container header={<Header variant="h3">Availability % by Service</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis domain={[minVal, 100]} tickFormatter={v => v + '%'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v.toFixed(2) + '%', 'Availability']} />
          <Bar dataKey="availability" radius={[4, 4, 0, 0]}>
            {metrics.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── MTTR chart ────────────────────────────────────────────────────────────────
function MttrChart({ metrics }: { metrics: ServiceMetric[] }) {
  return (
    <Container header={<Header variant="h3">Mean Time to Resolve (minutes)</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={metrics} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tickFormatter={v => v + 'm'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [Math.round(v) + 'm', 'MTTR']} />
          <Bar dataKey="mttr" fill="#38bdf8cc" stroke="#38bdf8" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Daily incident count ──────────────────────────────────────────────────────
function DailyChart({ dayCounts }: { dayCounts: { day: string; count: number }[] }) {
  return (
    <Container header={<Header variant="h3">Daily Incident Count</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dayCounts} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis dataKey="day" tick={{ fill: colorTextBodySecondary, fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis allowDecimals={false} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {dayCounts.map(d => (
              <Cell key={d.day} fill={d.count === 0 ? '#34d39966' : d.count <= 2 ? '#fbbf2499' : '#f4728699'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Downtime chart ────────────────────────────────────────────────────────────
function DowntimeChart({ metrics }: { metrics: ServiceMetric[] }) {
  const sorted = [...metrics].sort((a, b) => b.totalDowntimeMs - a.totalDowntimeMs)
  return (
    <Container header={<Header variant="h3">Total Downtime by Service</Header>}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 8, right: 16, left: 80, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colorBorderDividerDefault} />
          <XAxis type="number" tickFormatter={v => Math.round(v / 60) + 'h'} tick={{ fill: colorTextBodySecondary, fontSize: 11 }} />
          <YAxis type="category" dataKey="entity" tick={{ fill: colorTextBodySecondary, fontSize: 11 }} width={80} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatDuration(v), 'Downtime']} />
          <Bar dataKey="totalDowntimeMs" radius={[0, 4, 4, 0]}>
            {sorted.map(m => <Cell key={m.entity} fill={getColor(m.entity) + 'cc'} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}

// ── Active incident banner ────────────────────────────────────────────────────
function ActiveBanner({ incidents }: { incidents: GrokIncident[] }) {
  const active = incidents.filter(i => i.ongoing)
  if (!active.length) return null
  return (
    <Flashbar items={active.map(i => ({
      type: i.severity === 'down' ? 'error' as const : 'warning' as const,
      header: `Active: ${i.title}`,
      content: `Service: ${i.service} · Running for ${formatDuration(Date.now() - i.startDate.getTime())}`,
      dismissible: false,
      id: i.id,
      action: i.link
        ? <Button href={i.link} target="_blank" variant="inline-link">View →</Button>
        : undefined,
    }))} />
  )
}

// ── Incident table ────────────────────────────────────────────────────────────
function IncidentTable({ incidents }: { incidents: GrokIncident[] }) {
  const [filterText, setFilterText] = useState('')
  const [severityOption, setSeverityOption] = useState({ label: 'All Severities', value: '' })
  const [serviceOption, setServiceOption] = useState({ label: 'All Services', value: '' })
  const [currentPage, setCurrentPage] = useState(0)
  const PAGE_SIZE = 10

  const allServices = [...new Set(incidents.map(i => i.service))].sort()
  const serviceOptions = [
    { label: 'All Services', value: '' },
    ...allServices.map(s => ({ label: s, value: s })),
  ]
  const severityOptions = [
    { label: 'All Severities', value: '' },
    { label: 'Degraded', value: 'degraded' },
    { label: 'Down', value: 'down' },
  ]

  const filtered = incidents.filter(i => {
    if (filterText && !i.title.toLowerCase().includes(filterText.toLowerCase()) &&
        !i.service.toLowerCase().includes(filterText.toLowerCase())) return false
    if (severityOption.value && i.severity !== severityOption.value) return false
    if (serviceOption.value && i.service !== serviceOption.value) return false
    return true
  }).slice().reverse() // newest first

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const page = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)

  return (
    <Table
      columnDefinitions={[
        {
          id: 'title', header: 'Title', width: 300,
          cell: (item: GrokIncident) => item.link
            ? <a href={item.link} target="_blank" rel="noopener" style={{ color: '#38bdf8' }}>{item.title}</a>
            : item.title,
        },
        {
          id: 'service', header: 'Service',
          cell: (item: GrokIncident) => (
            <span style={{
              display: 'inline-block', padding: '1px 8px', borderRadius: 10,
              fontSize: 11, fontWeight: 600,
              background: getColor(item.service) + '22', color: getColor(item.service),
            }}>{item.service}</span>
          ),
        },
        {
          id: 'severity', header: 'Severity',
          cell: (item: GrokIncident) => (
            <StatusIndicator type={item.severity === 'down' ? 'error' : 'warning'}>
              {item.severity === 'down' ? 'Down' : 'Degraded'}
            </StatusIndicator>
          ),
        },
        { id: 'start', header: 'Start', cell: (item: GrokIncident) => fmtDateShort(item.startDate) },
        {
          id: 'end', header: 'End',
          cell: (item: GrokIncident) => item.ongoing
            ? <StatusIndicator type="in-progress">Ongoing</StatusIndicator>
            : item.resolvedDate ? fmtDateShort(item.resolvedDate) : '-',
        },
        {
          id: 'duration', header: 'Duration',
          cell: (item: GrokIncident) => formatDuration(item.durationMs) + (item.ongoing ? '+' : ''),
        },
        {
          id: 'status', header: 'Status',
          cell: (item: GrokIncident) => (
            <StatusIndicator type={
              item.status === 'resolved' ? 'success' :
              item.status === 'monitoring' ? 'info' :
              item.status === 'investigating' ? 'warning' : 'pending'
            }>
              {item.status}
            </StatusIndicator>
          ),
        },
      ]}
      items={page}
      filter={
        <SpaceBetween direction="horizontal" size="xs">
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Search incidents…"
            onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(0) }}
          />
          <Select
            selectedOption={severityOption}
            options={severityOptions}
            onChange={({ detail }) => { setSeverityOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
          <Select
            selectedOption={serviceOption}
            options={serviceOptions}
            onChange={({ detail }) => { setServiceOption(detail.selectedOption as any); setCurrentPage(0) }}
          />
        </SpaceBetween>
      }
      header={
        <Header
          counter={`(${filtered.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>«</Button>
              <Button disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)}>‹ Prev</Button>
              <Box padding={{ top: 'xs' }} color="text-body-secondary" fontSize="body-s">
                {currentPage + 1} / {totalPages}
              </Box>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>Next ›</Button>
              <Button disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)}>»</Button>
            </SpaceBetween>
          }
        >
          Incident Log
        </Header>
      }
      stripedRows
      sortingDisabled
      variant="container"
      empty={<Box textAlign="center" color="text-body-secondary">No incidents match your filters</Box>}
    />
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function GrokHealthPage() {
  const [allIncidents, setAllIncidents] = useState<GrokIncident[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchNote, setFetchNote] = useState('')
  const [period, setPeriod] = useState<'7' | '14' | '30' | '0'>('14')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  // ── Fetch RSS feed ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      let xmlText: string | null = null

      // Try direct fetch first
      try {
        const res = await fetch(RSS_URL)
        if (res.ok) xmlText = await res.text()
      } catch {
        // CORS likely blocked - fall through to proxy
      }

      // Fallback: CORS proxy
      if (!xmlText) {
        try {
          const res = await fetch(CORS_PROXY(RSS_URL))
          if (res.ok) xmlText = await res.text()
        } catch {
          // proxy also failed
        }
      }

      if (!xmlText) {
        throw new Error(
          'Could not fetch the xAI status feed. The RSS feed at status.x.ai may have CORS restrictions. ' +
          'Try using a CORS browser extension, or run in local dev mode where Vite can proxy the request.'
        )
      }

      const parsed = parseRssFeed(xmlText)
      setAllIncidents(parsed)

      const total = parsed.length
      const oldest = parsed[0]?.startDate.toISOString().slice(0, 10) ?? '?'
      const newest = parsed[parsed.length - 1]?.startDate.toISOString().slice(0, 10) ?? '?'
      setFetchNote(`${total} incidents · ${oldest} → ${newest}`)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // ── Window calculation ─────────────────────────────────────────────────────
  const { windowStart, windowEnd } = (() => {
    const now = new Date()
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
    if (customStart && customEnd) {
      return { windowStart: new Date(customStart + 'T00:00:00Z'), windowEnd: new Date(customEnd + 'T00:00:00Z') }
    }
    if (period === '0') {
      return { windowStart: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)), windowEnd: end }
    }
    return { windowStart: new Date(end.getTime() - parseInt(period) * 86400000), windowEnd: end }
  })()

  // ── Filter incidents to window ─────────────────────────────────────────────
  const incidents = allIncidents.filter(inc => {
    const end = inc.resolvedDate || windowEnd
    return end > windowStart && inc.startDate < windowEnd
  }).map(inc => {
    const endCapped = new Date(Math.min((inc.resolvedDate || windowEnd).getTime(), windowEnd.getTime()))
    const startClamped = new Date(Math.max(inc.startDate.getTime(), windowStart.getTime()))
    const durationMs = Math.max(0, endCapped.getTime() - startClamped.getTime())
    return { ...inc, endCapped, durationMs, durationMin: durationMs / 60000 }
  }).filter(inc => inc.durationMs > 10000) // drop sub-10s artifacts

  const windowMs = windowEnd.getTime() - windowStart.getTime()
  const windowDays = windowMs / 86400000

  // ── Service metrics ────────────────────────────────────────────────────────
  const allServices = [...new Set(allIncidents.map(i => i.service))].sort()
  const serviceMetrics: ServiceMetric[] = allServices.map(service => {
    const sInc = incidents.filter(i => i.service === service)
    const outageInc = sInc.filter(i => i.severity === 'down')
    const intervals = outageInc.map(i => [
      Math.max(i.startDate.getTime(), windowStart.getTime()),
      i.endCapped.getTime(),
    ] as [number, number])
    const merged = mergeIntervals(intervals)
    const totalDowntimeMs = merged.reduce((s, iv) => s + (iv[1] - iv[0]), 0)
    const availability = ((windowMs - totalDowntimeMs) / windowMs) * 100
    const mttr = sInc.length > 0 ? sInc.reduce((s, i) => s + i.durationMin, 0) / sInc.length : 0
    const longest = sInc.length > 0 ? sInc.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null
    return { entity: service, count: sInc.length, availability, mttr, totalDowntimeMs, longest }
  })

  // ── Global KPIs ────────────────────────────────────────────────────────────
  const downIncidents = incidents.filter(i => i.severity === 'down')
  const allIntervals = downIncidents.map(i => [
    Math.max(i.startDate.getTime(), windowStart.getTime()),
    i.endCapped.getTime(),
  ] as [number, number])
  const totalDowntimeMs = mergeIntervals(allIntervals).reduce((s, iv) => s + (iv[1] - iv[0]), 0)
  const globalMttr = incidents.length > 0 ? incidents.reduce((s, i) => s + i.durationMin, 0) / incidents.length : 0
  const globalLongest = incidents.length > 0 ? incidents.reduce((b, i) => i.durationMin > b.durationMin ? i : b) : null

  const dayCounts: { day: string; count: number }[] = []
  for (let d = new Date(windowStart); d < windowEnd; d = new Date(d.getTime() + 86400000)) {
    const dk = d.toISOString().slice(0, 10)
    dayCounts.push({ day: dk.slice(5), count: incidents.filter(i => i.startDate.toISOString().slice(0, 10) === dk).length })
  }
  const worstDay = [...dayCounts].sort((a, b) => b.count - a.count)[0] ?? { day: '-', count: 0 }

  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <SpaceBetween size="m" alignItems="center">
        <Spinner size="large" />
        <Box color="text-body-secondary">Fetching incidents from status.x.ai…</Box>
      </SpaceBetween>
    </div>
  )

  if (error) return (
    <div style={{ padding: 24 }}>
      <Alert type="error" header="Failed to load incident data" action={<Button onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    </div>
  )

  return (
    <div style={{ padding: 24 }}>
      <SpaceBetween size="l">

        {/* Header */}
        <Header
          variant="h1"
          description={
            <SpaceBetween direction="vertical" size="xxs">
              <Box color="text-body-secondary" fontSize="body-s">{fmtDate(windowStart)} – {fmtDate(windowEnd)} · {fetchNote}</Box>
              <Box color="text-body-secondary" fontSize="body-s">
                Live from{' '}
                <a href="https://status.x.ai" target="_blank" rel="noopener" style={{ color: '#38bdf8' }}>
                  status.x.ai
                </a>
              </Box>
            </SpaceBetween>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {(['7', '14', '30', '0'] as const).map(p => (
                <Button
                  key={p}
                  variant={period === p && !customStart ? 'primary' : 'normal'}
                  onClick={() => { setPeriod(p); setCustomStart(''); setCustomEnd('') }}
                >
                  {p === '0' ? 'YTD' : p + 'd'}
                </Button>
              ))}
              <Button iconName="refresh" onClick={load} />
            </SpaceBetween>
          }
        >
          xAI / Grok Service Health
        </Header>

        {/* Active incident banner */}
        <ActiveBanner incidents={allIncidents} />

        {/* KPI strip */}
        <Container header={<Header variant="h2">Summary</Header>}>
          <ColumnLayout columns={4} borders="vertical">
            <KpiCard label="Total Incidents" value={incidents.length} detail={`${Math.round(windowDays)}-day window`} />
            <KpiCard label="Total Downtime" value={formatDuration(totalDowntimeMs)} detail="merged intervals" />
            <KpiCard label="Global MTTR" value={Math.round(globalMttr) + 'm'} detail="avg per incident" />
            <KpiCard label="Full Outages" value={downIncidents.length} detail="down / major outage" />
            <KpiCard label="Worst Day" value={worstDay.day} detail={worstDay.count + ' incidents'} />
            <KpiCard label="Incidents / Day" value={(incidents.length / Math.max(windowDays, 1)).toFixed(1)} detail="average" />
            <KpiCard
              label="Longest Incident"
              value={globalLongest ? formatDuration(globalLongest.durationMs) : '-'}
              detail={globalLongest ? globalLongest.title.slice(0, 28) + '…' : ''}
              onClick={globalLongest?.link ? () => window.open(globalLongest.link, '_blank', 'noopener') : undefined}
            />
            <KpiCard label="Services Tracked" value={allServices.length} detail="from RSS feed" />
          </ColumnLayout>
        </Container>

        {/* Entity cards */}
        <EntityCards metrics={serviceMetrics} />

        {/* Charts row 1 */}
        <ColumnLayout columns={2}>
          <AvailabilityChart metrics={serviceMetrics} />
          <MttrChart metrics={serviceMetrics} />
        </ColumnLayout>

        {/* Charts row 2 */}
        <ColumnLayout columns={2}>
          <DailyChart dayCounts={dayCounts} />
          <DowntimeChart metrics={serviceMetrics} />
        </ColumnLayout>

        {/* Incident table */}
        <IncidentTable incidents={incidents} />

      </SpaceBetween>
    </div>
  )
}
